import { FormControl, FormErrorMessage, Switch, Text } from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import type { AdminSettings } from "../../types/adminsettings.types";
import Input from "../inputs/Input";
import { useImperativeHandle, forwardRef } from "react";

interface AdminSettingsFormProps {
	value?: AdminSettings | null | undefined;
}

export type AdminSettingsFormHandler = {
	values: () => AdminSettings;
	valid: () => Promise<boolean>;
};

const AdminSettingsForm = forwardRef<
	AdminSettingsFormHandler,
	AdminSettingsFormProps
>(({ value }, ref) => {
	const methods = useForm();

	useImperativeHandle(ref, () => ({
		valid: () => {
			return methods.trigger();
		},
		values: () => {
			const values = methods.getValues();
			return {
				baseDiscountPercentage: values.baseDiscountPercentage / 100,
				shippingAfter: values.shippingAfter,
				upsShippingPrice: values.upsShippingPrice * 100,
				upsSageId: values.upsSageId,
				printingServiceAddress: values.printingServiceAddress,
				expressPrice: values.expressPrice * 100,
				expressSageId: values.expressSageId,
				meanDaysReceiptTime: values.meanDaysReceiptTime,
				meanDaysExpressProductionTime: values.meanDaysExpressProductionTime,
				meanDaysProductionTime: values.meanDaysProductionTime,
				meanDaysShippingTime: values.meanDaysShippingTime,
				disableCommandCreation: Boolean(values.disableCommandCreation),
			};
		},
	}));
	return (
		<FormProvider {...methods}>
			<form style={{ marginTop: "32px" }}>
				<FormControl
					isInvalid={!!methods.formState.errors.baseDiscountPercentage}
					mb={4}
				>
					<Input
						id="baseDiscountPercentage"
						placeholder="Remise refonte (%)"
						defaultValue={
							value?.baseDiscountPercentage
								? value?.baseDiscountPercentage * 100
								: 0
						}
						{...methods.register("baseDiscountPercentage", {
							required: "Ce champ est obligatoire",
						})}
					></Input>
					<FormErrorMessage>
						<>{methods.formState.errors?.baseDiscountPercentage?.message}</>
					</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={!!methods.formState.errors.upsSageId} mb={4}>
					<Input
						id="upsSageId"
						placeholder="UPS (Sage ID)"
						defaultValue={value?.upsSageId}
						{...methods.register("upsSageId", {
							required: "Ce champ est obligatoire",
						})}
					></Input>
					<FormErrorMessage>
						<>{methods.formState.errors?.upsSageId?.message}</>
					</FormErrorMessage>
				</FormControl>

				<FormControl
					isInvalid={!!methods.formState.errors.upsShippingPrice}
					mb={4}
				>
					<Input
						id="upsShippingPrice"
						placeholder="UPS (Prix de livraison)"
						defaultValue={
							value?.upsShippingPrice ? value.upsShippingPrice / 100 : 0
						}
						{...methods.register("upsShippingPrice", {
							required: "Ce champ est obligatoire",
						})}
					></Input>
					<FormErrorMessage>
						<>{methods.formState.errors?.upsShippingPrice?.message}</>
					</FormErrorMessage>
				</FormControl>

				<FormControl
					isInvalid={!!methods.formState.errors.expressSageId}
					mb={4}
				>
					<Input
						id="expressSageId"
						placeholder="Express (Sage ID)"
						defaultValue={value?.expressSageId}
						{...methods.register("expressSageId", {
							required: "Ce champ est obligatoire",
						})}
					></Input>
					<FormErrorMessage>
						<>{methods.formState.errors?.expressSageId?.message}</>
					</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={!!methods.formState.errors.expressPrice} mb={4}>
					<Input
						id="expressPrice"
						placeholder="Express (Prix de livraison)"
						type="number"
						defaultValue={value?.expressPrice ? value.expressPrice / 100 : 0}
						{...methods.register("expressPrice", {
							required: "Ce champ est obligatoire",
						})}
					></Input>
					<FormErrorMessage>
						<>{methods.formState.errors?.expressPrice?.message}</>
					</FormErrorMessage>
				</FormControl>

				<FormControl
					isInvalid={!!methods.formState.errors.meanDaysReceiptTime}
					mb={4}
				>
					<Input
						id="meanDaysReceiptTime"
						placeholder="Temps de réception moyen"
						min={0}
						type="number"
						defaultValue={value?.meanDaysReceiptTime}
						{...methods.register("meanDaysReceiptTime", {
							required: "Ce champ est obligatoire",
						})}
					></Input>
					<FormErrorMessage>
						<>{methods.formState.errors?.meanDaysReceiptTime?.message}</>
					</FormErrorMessage>
				</FormControl>

				<FormControl
					isInvalid={!!methods.formState.errors.meanDaysProductionTime}
					mb={4}
				>
					<Input
						id="meanDaysProductionTime"
						placeholder="Temps de production moyen"
						min={0}
						type="number"
						defaultValue={value?.meanDaysProductionTime}
						{...methods.register("meanDaysProductionTime", {
							required: "Ce champ est obligatoire",
						})}
					></Input>
					<FormErrorMessage>
						<>{methods.formState.errors?.meanDaysProductionTime?.message}</>
					</FormErrorMessage>
				</FormControl>

				<FormControl
					isInvalid={!!methods.formState.errors.meanDaysProductionTime}
					mb={4}
				>
					<Input
						id="meanDaysExpressProductionTime"
						placeholder="Temps de production moyen en mode prioritaire"
						min={0}
						type="number"
						defaultValue={value?.meanDaysExpressProductionTime}
						{...methods.register("meanDaysExpressProductionTime", {
							required: "Ce champ est obligatoire",
						})}
					></Input>
					<FormErrorMessage>
						<>
							{methods.formState.errors?.meanDaysExpressProductionTime?.message}
						</>
					</FormErrorMessage>
				</FormControl>

				<FormControl
					isInvalid={!!methods.formState.errors.meanDaysShippingTime}
					mb={4}
				>
					<Input
						id="meanDaysShippingTime"
						placeholder="Temps d'expédition moyen"
						min={0}
						type="number"
						defaultValue={value?.meanDaysShippingTime}
						{...methods.register("meanDaysShippingTime", {
							required: "Ce champ est obligatoire",
						})}
					></Input>
					<FormErrorMessage>
						<>{methods.formState.errors?.meanDaysShippingTime?.message}</>
					</FormErrorMessage>
				</FormControl>

				<FormControl display="flex" alignItems="center" mb={4}>
					<Switch
						id="disableCommandCreation"
						colorScheme="brand"
						defaultChecked={value?.disableCommandCreation ?? false}
						{...methods.register("disableCommandCreation", {
							setValueAs: (val) => val === true || val === "on",
						})}
					></Switch>
					<Text ml={2}>Désactiver la création de commandes</Text>
				</FormControl>

				<FormControl
					isInvalid={!!methods.formState.errors.printingServiceAddress}
				>
					<Input
						id="printingServiceAddress"
						placeholder="Adresse service impression"
						defaultValue={value?.printingServiceAddress}
						{...methods.register("printingServiceAddress", {
							required: "Ce champ est obligatoire",
						})}
					></Input>
					<FormErrorMessage>
						<>{methods.formState.errors?.printingServiceAddress?.message}</>
					</FormErrorMessage>
				</FormControl>
			</form>
		</FormProvider>
	);
});

export default AdminSettingsForm;
